import { IDeferred, IHttpService, IPromise, IQService, IRootScopeService } from "angular";
import { EnvironmentService, IAppConfig } from "@gtmhub/env";
import { initialize as initializeLDClient, LDClient, LDOptions } from "launchdarkly-js-client-sdk";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { getCurrentUserId } from "@gtmhub/users";
import { IAccount, AccountType } from "@gtmhub/core";
import { UserSignatureResponse } from "./models";

export class FeatureToggleService {
  public static $inject = ["$rootScope", "$q", "$http", "AccountResolverService", "EnvironmentService", "appConfig", "AccountService"];

  private ldClient: LDClient;

  private initializationFinished: IDeferred<void>;

  constructor(
    private $rootScope: IRootScopeService,
    private $q: IQService,
    private $http: IHttpService,
    private accountResolverService: AccountResolverService,
    private env: EnvironmentService,
    private appConfig: IAppConfig,
    private accountService: AccountService
  ) {
    this.initializationFinished = this.$q.defer<void>();
  }

  public initialize(): void {
    if (!this.appConfig.launchDarkly?.enabled) {
      return;
    }
    const accountData: IAccount = this.accountResolverService.getAccountData();

    const user = {
      key: getCurrentUserId(),
      custom: {
        account_id: accountData.id,
        account_type: this.getAccountType(accountData.type),
        edition: accountData.edition.name,
      },
    };

    const accountSettingsFeatures = this.accountService.getAccountSetting<{ key: string }>("features");
    !!accountSettingsFeatures &&
      Object.keys(accountSettingsFeatures).forEach((k) => {
        user.custom[`module-${k}`] = accountSettingsFeatures[k];
      });

    this.getUserSignature().then((signature) => {
      const ldSettings: LDOptions = {
        hash: signature,
        streaming: false,
        sendEvents: true,
      };

      if (this.appConfig.launchDarkly.proxyBaseUrl) {
        ldSettings.baseUrl = this.appConfig.launchDarkly.proxyBaseUrl;
      }

      if (this.appConfig.launchDarkly.eventBaseUrl) {
        ldSettings.eventsUrl = this.appConfig.launchDarkly.eventBaseUrl;
      }

      this.ldClient = initializeLDClient(this.appConfig.launchDarkly.clientKey, user, ldSettings);

      this.initializationFinished.resolve();
      this.$rootScope.$broadcast("FeatureFlagUpdated");
    });
  }

  public isFeatureAvailable(featureKey: string): IPromise<boolean> {
    return this.waitUntilReady().then(() => this.ldClient.variation(featureKey, false));
  }

  private waitUntilReady(): IPromise<void> {
    return this.initializationFinished.promise.then(() => {
      return this.$q.when(this.ldClient.waitUntilReady());
    });
  }

  private getUserSignature(): IPromise<string> {
    const url = this.env.getApiEndpoint(`/feature-toggler/user/secure-id`);

    return this.$http.get<UserSignatureResponse>(url).then(
      (response) => response.data.secureId,
      (err) => {
        console.error(err);
        // we still want the LauchDarkly client to be intialized even in case of error
        return "";
      }
    );
  }

  private getAccountType(accountType: AccountType): string {
    let result: string;
    switch (accountType) {
      case 0:
        result = "TrialAccount";
        break;
      case 1:
        result = "ClientAccount";
        break;
      case 2:
        result = "InternalAccount";
        break;
      case 3:
        result = "DemoAccount";
        break;
      case 4:
        result = "PartnerAccount";
        break;
      case 5:
        result = "FreeAccount";
        break;
      default:
        result = "InternalAccount";
    }

    return result;
  }
}
